import { Component, OnDestroy, ViewChild } from '@angular/core';
import { LedgerPostsComponent } from 'components/accounting/ledger/ledger-posts.component';
import { CustomSelectComponent } from 'components/input/custom-select/custom-select.component';
import { SubmitButtonComponent } from 'components/input/submit-button/submit-button.component';
import { CredentialType } from 'enums/CredentialType';
import { MessageType } from 'enums/MessageType';
import { AccountingPlanModel } from 'models/generated/Entities.Accounting.AccountingPlan';
import { ProductPlanModel } from 'models/generated/Entities.GormCrm.ProductPlan';
import { SelectListItemModel } from 'models/SelectList';
import { Layout } from 'modules/layout';
import { PaymentIntervalPipe } from 'pipes/payment-interval.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { Subscription } from 'rxjs';
import { AccountingService } from 'services/accounting.service';
import { CredentialRequired } from 'services/credential.service';
import { FormValidationService } from 'services/form-validation.service';
import { NavigationService } from 'services/navigation.service';
import { NotificationService } from 'services/notification.service';
import { ProductPlanService } from 'services/productplan.service';
import { PushService } from 'services/push.service';

@CredentialRequired(CredentialType.EditAccountingPlan)
@Component({
	templateUrl: './edit-invoice-interval.component.html',
	providers: [FormValidationService],
	imports: [Layout, CustomSelectComponent, LedgerPostsComponent, TranslatePipe]
})
export class EditInvoiceIntervalComponent implements OnDestroy {

	@ViewChild('ledgerPosts') ledgerPosts?: LedgerPostsComponent;
	@ViewChild(SubmitButtonComponent) submitButton?: SubmitButtonComponent;

	selectedInterval?: number;
	maxNumberOfPayments?: number;

	availableIntervals: SelectListItemModel[];
	availableMaxPaymentNumbers: SelectListItemModel[];
	generatingPlan = false;
	onAccountingPlanChanges: Subscription;

	private _accountingPlan?: AccountingPlanModel;
	private _productPlan?: ProductPlanModel;

	set accountingPlan(value: AccountingPlanModel | undefined) {
		this._accountingPlan = value;
		this.selectedInterval = value?.paymentInterval;
		this.maxNumberOfPayments = value?.numberOfInstallments;
	}
	get accountingPlan() { return this._accountingPlan; }

	set productPlan(value: ProductPlanModel | undefined) {
		this._productPlan = value;
	}
	get productPlan() { return this._productPlan; }

	reload() {
		if (this.accountingPlan) {
			this.selectedInterval = undefined;
			this.notification.warning("edit-invoice-interval.reload-on-changes-warning");
			this.accountingService.getAccountingPlan(this.accountingPlan!.id!).then(plan => {
				this.accountingPlan = plan;
				this.generateBillingPlan();
			});
		}
	}

	generateBillingPlan() {
		if (!this.ledgerPosts || !this.accountingPlan) return;
		this.generatingPlan = true;

		this.accountingService.generateSuggestedBillingPlan(this.accountingPlan.id!, this.selectedInterval!, this.maxNumberOfPayments).then(newLedgerPosts => {
			this.ledgerPosts!.reloadLedgerPosts().then(() => {
				this.ledgerPosts!.clearEditablePosts();
				newLedgerPosts.forEach(post => this.ledgerPosts!.addPost(post));
				this.generatingPlan = false;
			});
		});
	}

	cancel() {
		this.navigation.closeView(this);
	}

	updateBillingPlan() {
		if (this.selectedInterval === undefined || this.selectedInterval === null || !this.accountingPlan) return;
		if (this.accountingPlan.startDate && this.ledgerPosts) {
			this.accountingService.updateBillingPlanAndInterval(this.accountingPlan.id!, this.selectedInterval, this.maxNumberOfPayments, this.ledgerPosts.getEditedLedgerPosts(), this.accountingPlan.totalPlanValue).then(() => {
				this.navigation.closeView(this);
			}).catch(() => {
				this.submitButton?.reset();
			});
		} else if (this.productPlan) {
			this.productPlanService.updateProductPlanPaymentScheme(this.productPlan.id!, this.selectedInterval).then(() => {
				this.navigation.closeView(this);
			}).catch(() => {
				this.submitButton?.reset();
			});
		}
	}

	constructor(
		private readonly accountingService: AccountingService,
		private readonly productPlanService: ProductPlanService,
		private readonly navigation: NavigationService,
		private readonly notification: NotificationService,
		private readonly pushService: PushService,
		paymentInterval: PaymentIntervalPipe) {

		this.availableIntervals = [
			new SelectListItemModel(1, paymentInterval.transform(1)),
			new SelectListItemModel(3, paymentInterval.transform(3)),
			new SelectListItemModel(12, paymentInterval.transform(12)),
			new SelectListItemModel(0, paymentInterval.transform(0)),
		];

		this.availableMaxPaymentNumbers = [...Array.from(Array(12).keys()).map(i => new SelectListItemModel(i + 1, (i + 1).toString()))];

		this.onAccountingPlanChanges = this.pushService.onChanges<{ accountingPlanId: string, companyId: string }>(MessageType.AccountingPlanChanges).subscribe(async (event) => {
			if (this.accountingPlan?.id === event.data?.accountingPlanId) this.reload();
		});
	}

	ngOnDestroy() {
		this.onAccountingPlanChanges.unsubscribe();
	}
}
